import { useEffect, useRef, useState } from "react";

/**
 * Scroll-scrubbed card animation.
 *
 * A pre-rendered 204-frame AVIF sequence (see `scripts/build-card-animation.py`)
 * painted to a canvas. The section is several viewports tall; the canvas sticks
 * to the top of the scroll viewport while you scroll through it and the scroll
 * progress picks the frame — scrolling down plays the card flip forward,
 * scrolling back up rewinds it.
 *
 * The homepage is not scrolled by the window: `#root` is `overflow: hidden` and
 * the homepage container is the scroller, so we resolve the nearest scrollable
 * ancestor at mount and measure against that.
 *
 * AVIF is the only encoding served. It measured closer to the source PNGs than
 * the WebP set it replaced (44.6 dB vs 43.9 dB at 1024px) at ~52% of the bytes.
 * Browsers that cannot decode AVIF — Safari before 16.4, older Edge/Firefox —
 * get a blank canvas rather than a fallback; there is no second sequence.
 */

export interface FrameStats {
  /** Frame width actually being served (1024 on desktop, 512 on phones). */
  width: number;
  loaded: number;
  total: number;
  /** Bytes over the wire so far, from resource timing. */
  bytes: number;
}

const FRAME_COUNT = 204;

/**
 * The render is not centred inside its own frames. The crop is the union of
 * every frame's alpha box, and the card swings hard during the flip — out to
 * x=8 at frame 89 and x=1014 at frame 67 of 1024 — so the union is centred but
 * the resting pose (frames 1 and 204, what you see before scrolling) sits at
 * 35..919, i.e. 3.4% left of centre.
 *
 * Cropping tighter would clip the swing, so the canvas is padded on the left
 * and trimmed on the right instead: 1024 + 60 - 10 = 1074 wide, image drawn at
 * x=60. That puts the resting card at 95..979 — equal gaps — while frame 67
 * still lands exactly flush with the right edge rather than being cut.
 */
const FRAME_PAD_LEFT = 60 / 1024;
const FRAME_TRIM_RIGHT = 10 / 1024;
const ASPECT = 1.4834; // padded canvas: 1074 / 724
/** Section height as a multiple of the scroll viewport: 1 sticky screen + 3.2 screens of scrubbing. */
const SCROLL_LENGTH = 4.2;
/** In-flight frame requests while prefetching the sequence. */
const CONCURRENCY = 8;

// Mirrors `out_dir()` in the encoder script.
const frameSrc = (width: number, index: number) =>
  `/animation/card/avif/${width}/${String(index + 1).padStart(4, "0")}.avif`;

/** Transferred size of an already-loaded same-origin image, 0 if unavailable. */
const transferredBytes = (url: string) => {
  const entry = performance
    .getEntriesByName(url, "resource")
    .pop() as PerformanceResourceTiming | undefined;
  return entry?.encodedBodySize || 0;
};

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const getScrollParent = (node: HTMLElement | null): HTMLElement | Window => {
  let el = node?.parentElement ?? null;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return window;
};

interface Props {
  /** Called as frames arrive, for pages that want to show payload numbers. */
  onStats?: (stats: FrameStats) => void;
}

const CardAnimation = ({ onStats }: Props) => {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const framesRef = useRef<Array<HTMLImageElement | undefined>>([]);
  /** Frame the current scroll position asks for. */
  const targetRef = useRef(0);
  /** Frame actually on the canvas (-1 = nothing painted yet). */
  const drawnRef = useRef(-1);
  const paintRef = useRef<() => void>(() => {});
  const rafRef = useRef(0);
  const [loaded, setLoaded] = useState(0);

  // Held in a ref so an inline `onStats` from the parent can't restart the
  // whole download on every render.
  const statsRef = useRef(onStats);
  statsRef.current = onStats;

  // Paint the loaded frame closest to the one the scroll position asks for, so
  // the canvas stays responsive while the sequence is still downloading and
  // sharpens up as the exact frames arrive.
  useEffect(() => {
    paintRef.current = () => {
      const frames = framesRef.current;
      const target = targetRef.current;
      let index = -1;
      if (frames[target]) {
        index = target;
      } else {
        for (let d = 1; d < FRAME_COUNT; d += 1) {
          if (frames[target - d]) {
            index = target - d;
            break;
          }
          if (frames[target + d]) {
            index = target + d;
            break;
          }
        }
      }
      if (index < 0 || index === drawnRef.current) return;

      const img = frames[index];
      const canvas = canvasRef.current;
      if (!img || !canvas) return;
      const padX = Math.round(img.naturalWidth * FRAME_PAD_LEFT);
      const width = Math.round(
        img.naturalWidth * (1 + FRAME_PAD_LEFT - FRAME_TRIM_RIGHT)
      );
      if (canvas.width !== width) {
        canvas.width = width;
        canvas.height = img.naturalHeight;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Overhangs the right edge by FRAME_TRIM_RIGHT, which is transparent.
      ctx.drawImage(img, padX, 0);
      drawnRef.current = index;
    };
  }, []);

  // Fetch the sequence at the size this device actually needs.
  useEffect(() => {
    // Deliberately keyed off CSS width, not device pixels: the 1024 set is
    // ~1.9 MB and the 512 set ~0.9 MB, so phones stay on the light one even at
    // 3x DPR (512 over a ~360 px canvas still reads as ~1.4x).
    const saveData = (navigator as any).connection?.saveData === true;
    const width = !saveData && window.innerWidth > 700 ? 1024 : 512;
    const frames: Array<HTMLImageElement | undefined> = new Array(FRAME_COUNT);
    framesRef.current = frames;

    let cancelled = false;
    let done = 0;
    let bytes = 0;
    let next = 0;

    const loadOne = (index: number) =>
      new Promise<void>((resolve) => {
        const img = new Image();
        img.decoding = "async";
        const settle = (ok: boolean) => {
          if (cancelled) return resolve();
          if (ok) {
            frames[index] = img;
            bytes += transferredBytes(img.src);
          }
          done += 1;
          setLoaded(done);
          statsRef.current?.({
            width,
            loaded: done,
            total: FRAME_COUNT,
            bytes,
          });
          if (ok) paintRef.current();
          resolve();
        };
        img.onload = () => settle(true);
        img.onerror = () => settle(false);
        img.src = frameSrc(width, index);
      });

    const pump = async () => {
      while (!cancelled && next < FRAME_COUNT) {
        const index = next;
        next += 1;
        // eslint-disable-next-line no-await-in-loop
        await loadOne(index);
      }
    };

    // First frame first so the hero is never blank, then the rest in order.
    loadOne(0).then(() => {
      if (cancelled) return;
      next = 1;
      for (let i = 0; i < CONCURRENCY; i += 1) pump();
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll scrubbing.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const scroller = getScrollParent(section);
    const isWindow = scroller === window;

    const viewportHeight = () =>
      isWindow ? window.innerHeight : (scroller as HTMLElement).clientHeight;

    const viewportTop = () =>
      isWindow ? 0 : (scroller as HTMLElement).getBoundingClientRect().top;

    const applyHeights = () => {
      const vh = viewportHeight();
      if (!vh) return;
      section.style.setProperty("--sticky-h", `${vh}px`);
      section.style.height = `${Math.round(vh * SCROLL_LENGTH)}px`;
    };

    // The page container pads its content (32px on the homepage, 16px on
    // /avif). On a 390px phone that padding was costing the card ~17% of the
    // viewport, so the sticky pane cancels it out and goes full-bleed. Measured
    // rather than hard-coded so both hosts stay correct.
    const applyBleed = () => {
      const host = section.parentElement;
      if (!host) return;
      const cs = getComputedStyle(host);
      const pad = Math.min(
        parseFloat(cs.paddingLeft) || 0,
        parseFloat(cs.paddingRight) || 0
      );
      section.style.setProperty("--bleed", `${pad}px`);
    };

    const update = () => {
      rafRef.current = 0;
      const rect = section.getBoundingClientRect();
      const travel = rect.height - viewportHeight();
      if (travel <= 0) return;
      const progress = clamp((viewportTop() - rect.top) / travel, 0, 1);
      targetRef.current = Math.round(progress * (FRAME_COUNT - 1));
      paintRef.current();
    };

    const schedule = () => {
      if (!rafRef.current) rafRef.current = requestAnimationFrame(update);
    };

    const onResize = () => {
      applyHeights();
      applyBleed();
      schedule();
    };

    applyHeights();
    applyBleed();
    schedule();

    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, []);

  const progress = Math.round((loaded / FRAME_COUNT) * 100);

  // `shrink-0` matters: the homepage is a flex column, which would otherwise
  // squash this tall section back down to one screen, leaving nothing to scrub.
  return (
    <section
      ref={sectionRef}
      aria-label="Touch Talk NFC card"
      className="relative w-full shrink-0"
      style={{ height: "300vh" }}
    >
      <div
        className="sticky top-0 flex w-full flex-col items-center justify-center"
        style={{
          height: "var(--sticky-h, 100vh)",
          // Full-bleed: undo the host container's padding (see applyBleed).
          marginInline: "calc(var(--bleed, 0px) * -1)",
          width: "calc(100% + var(--bleed, 0px) * 2)",
        }}
      >
        <canvas
          ref={canvasRef}
          className="w-full"
          style={{
            aspectRatio: String(ASPECT),
            // Edge-to-edge on phones, capped at 1090px on desktop — scaled up
            // from 1040 by the padding above so the card itself is unchanged.
            maxWidth: "min(100vw, 1090px)",
            maxHeight: "88%",
          }}
        />
        <div
          className="mt-2 h-[2px] w-24 overflow-hidden rounded-full bg-white/10 transition-opacity duration-500"
          style={{ opacity: progress >= 100 ? 0 : 1 }}
        >
          <div
            className="h-full bg-primary transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </section>
  );
};

export default CardAnimation;
